import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from 'react-query';
import api from '../../services/api';
import { 
  ChartBarIcon, 
  UserGroupIcon, 
  HeartIcon, 
  ChatBubbleLeftRightIcon,
  PlusIcon,
  EyeIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalFavorites: 0,
    totalComments: 0,
    totalViews: 0,
    recentProjects: [],
    recentFavorites: [],
    recentComments: []
  });

  // Récupérer les statistiques utilisateur
  const { data: userStats, isLoading: statsLoading } = useQuery(
    'userStats',
    async () => {
      const response = await api.get('/users/stats');
      return response.data;
    },
    {
      enabled: !!user,
      refetchInterval: 30000 // Rafraîchir toutes les 30 secondes
    }
  );

  // Récupérer les projets récents
  const { data: recentProjects } = useQuery(
    'recentProjects',
    async () => {
      const response = await api.get('/projects?limit=5&sortBy=createdAt&sortOrder=desc');
      return response.data;
    },
    {
      enabled: !!user
    }
  );

  // Récupérer les favoris récents
  const { data: recentFavorites } = useQuery(
    'recentFavorites',
    async () => {
      const response = await api.get(`/users/${user?.id}/favorites?limit=5`);
      return response.data;
    },
    {
      enabled: !!user
    }
  );

  useEffect(() => {
    if (userStats) {
      setStats(userStats);
    }
  }, [userStats]);

  if (statsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text mb-2">
            Bienvenue, {user?.firstName} !
          </h1>
          <p className="text-gray-600">
            Voici un aperçu de votre activité sur la Bourse d'Idées Innovactive 360°
          </p>
        </div>

        {/* Statistiques principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <ChartBarIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Mes Projets</p>
                <p className="text-2xl font-bold text-text">{stats.totalProjects}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100">
                <HeartIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Favoris</p>
                <p className="text-2xl font-bold text-text">{stats.totalFavorites}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Commentaires</p>
                <p className="text-2xl font-bold text-text">{stats.totalComments}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <EyeIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Vues</p>
                <p className="text-2xl font-bold text-text">{stats.totalViews}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions rapides */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 mb-8">
          <h2 className="text-xl font-semibold text-text mb-4">Actions Rapides</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex items-center p-4 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors">
              <PlusIcon className="h-6 w-6 text-primary-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-text">Nouveau Projet</p>
                <p className="text-sm text-gray-600">Soumettre une idée</p>
              </div>
            </button>
            
            <button className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
              <UserGroupIcon className="h-6 w-6 text-blue-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-text">Réseautage</p>
                <p className="text-sm text-gray-600">Contacter des investisseurs</p>
              </div>
            </button>
            
            <button className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
              <ArrowTrendingUpIcon className="h-6 w-6 text-green-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-text">Analytics</p>
                <p className="text-sm text-gray-600">Voir les performances</p>
              </div>
            </button>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Projets récents */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-text">Mes Projets Récents</h2>
            </div>
            <div className="p-6">
              {recentProjects?.length > 0 ? (
                <div className="space-y-4">
                  {recentProjects.map((project) => (
                    <div key={project.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h3 className="font-medium text-text">{project.title}</h3>
                        <p className="text-sm text-gray-600">{project.category}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">{new Date(project.createdAt).toLocaleDateString()}</p>
                        <p className="text-xs text-gray-500">{project.views} vues</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucun projet pour le moment</p>
                  <button className="mt-4 btn btn-primary btn-sm">
                    Créer mon premier projet
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Favoris récents */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-text">Mes Favoris Récents</h2>
            </div>
            <div className="p-6">
              {recentFavorites?.length > 0 ? (
                <div className="space-y-4">
                  {recentFavorites.map((favorite) => (
                    <div key={favorite.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h3 className="font-medium text-text">{favorite.project.title}</h3>
                        <p className="text-sm text-gray-600">par {favorite.project.owner?.firstName}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">{new Date(favorite.createdAt).toLocaleDateString()}</p>
                        <HeartIcon className="h-4 w-4 text-red-500 mx-auto mt-1" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <HeartIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucun favori pour le moment</p>
                  <button className="mt-4 btn btn-primary btn-sm">
                    Explorer les projets
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Graphique de performance */}
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-text">Performance de vos Projets</h2>
          </div>
          <div className="p-6">
            <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center">
                <ChartBarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Graphique de performance</p>
                <p className="text-sm text-gray-500">Bientôt disponible</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;